import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useLantern, useSession } from "../hooks";
import { sortedReplies } from "../sync/store";
import { taskPayloadSchema } from "../sync/types";
import { grouped } from "../timeline";
import { CatchUp } from "./CatchUp";
import { Composer } from "./Composer";
import { MessageItem } from "./MessageItem";
import { TaskCard } from "./TaskCard";
import { Pending } from "./Pending";
import { Resizer, threadEdge, useWidth } from "./Resizer";
import { IconClose } from "./icons";
import { t, tn } from "../i18n";

export function ThreadPanel({ channel, root, onClose }: { channel: string; root: string; onClose: () => void }) {
  const session = useSession();
  const state = useLantern();
  const rootMsg = state.channels.get(channel)?.messages.get(root);
  const replies = sortedReplies(state, root);
  const pending = [...state.pending.values()].filter((p) => p.threadRootID === root);
  const list = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useWidth(threadEdge);

  useEffect(() => {
    void session.engine.openThread(channel, root);
  }, [channel, root, session]);
  // Tasks asked for in the thread show under its root; a task the root
  // itself shows is left out. New ones arrive by their task events.
  const ownTask = rootMsg?.payload_schema === taskPayloadSchema ? (rootMsg.payload as { task_id?: string } | null)?.task_id : undefined;
  const tasks = [...state.tasks.values()]
    .filter((x) => x.thread?.channel_id === channel && x.thread.thread_root_id === root && x.id !== ownTask)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
  useEffect(() => {
    let live = true;
    session.api.threadTasks(channel, root).then(
      (r) => live && r.tasks.length > 0 && session.store.update((s) => r.tasks.forEach((x) => s.tasks.has(x.id) || s.tasks.set(x.id, x))),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [channel, root, session]);
  useLayoutEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [replies.length, pending.length]);
  // Seeing the thread reads it, up to its newest reply.
  const newest = Math.max(rootMsg?.last_reply_seq ?? 0, replies.at(-1)?.seq ?? 0);
  useEffect(() => {
    if (document.visibilityState === "visible") session.markThreadRead(channel, root, newest);
  }, [channel, root, newest, session]);
  useEffect(() => {
    // Escape closes the thread, unless it is for a dialog or a draft.
    const esc = (e: KeyboardEvent) =>
      e.key === "Escape" && !document.querySelector('[aria-modal="true"]') && !(e.target instanceof HTMLTextAreaElement && e.target.value) && onClose();
    addEventListener("keydown", esc);
    return () => removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <aside className="thread" aria-label={t("Thread")} style={{ "--thread-width": `${width}px` } as CSSProperties}>
      <Resizer edge={threadEdge} width={width} onResize={setWidth} label={t("Resize the thread")} />
      <header>
        <div className="thread-title">
          <h2>{t("Thread")}</h2>
          <span className="muted">{session.channelName(channel)}</span>
        </div>
        <FollowButton key={"follow-" + root} channel={channel} root={root} />
        <CatchUp key={root} channel={channel} threadRoot={root} />
        <button className="icon-button" onClick={onClose} aria-label={t("Close thread")} title={t("Close thread")}>
          <IconClose />
        </button>
      </header>
      <div className="messages" ref={list}>
        {rootMsg && <MessageItem m={rootMsg} />}
        {tasks.length > 0 && (
          <div className="thread-tasks">
            {tasks.map((x) => (
              <TaskCard key={x.id} id={x.id} />
            ))}
          </div>
        )}
        {replies.length > 0 && (
          <div className="reply-divider" role="separator">
            <span>{tn(replies.length, "{n} reply", "{n} replies")}</span>
          </div>
        )}
        {replies.map((m, i) => (
          <MessageItem key={m.id} m={m} compact={grouped(replies[i - 1], m)} />
        ))}
        <Pending items={pending} />
      </div>
      <Composer key={root} channel={channel} threadRoot={root} placeholder={t("Reply…")} />
    </aside>
  );
}

// FollowButton follows a thread, so its replies show in Threads and notify,
// or stops following it.
function FollowButton({ channel, root }: { channel: string; root: string }) {
  const session = useSession();
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    session.api.following(channel, root).then(
      (r) => live && setOn(r.following),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [session, channel, root]);
  if (on === null) return null;
  return (
    <button
      className={`small ${on ? "secondary" : ""}`}
      aria-pressed={on}
      title={on ? t("Stop getting notified about new replies") : t("Get notified about new replies")}
      onClick={() => {
        setOn(!on);
        session.api.follow(channel, root, !on).catch(() => setOn(on));
      }}
    >
      {on ? t("Following") : t("Follow")}
    </button>
  );
}
